import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, X, ArrowUp, ArrowDown, Upload, Link2, GripVertical, Monitor, Tablet, Smartphone } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

const PLACEMENTS = [["hero", "Hero Slider"], ["mid", "Homepage Mid Strip"], ["megamenu", "Mega Menu Promo"], ["collection", "Collection Banner"]];
const DEVICES = [["desktop", "Desktop", 1280, Monitor], ["tablet", "Tablet", 768, Tablet], ["mobile", "Mobile", 390, Smartphone]];

async function uploadFiles(files) {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  const { data } = await api.post("/admin/media/bulk", fd, { timeout: 300000 });
  return data.items || [];
}

function ImageField({ label, value, onChange, required, testid, hint }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const ref = useRef(null);
  const pick = async (files) => {
    const f = [...(files || [])].find((x) => x.type.startsWith("image/"));
    if (!f) return;
    setBusy(true);
    try {
      const items = await uploadFiles([f]);
      if (items[0]?.url) { onChange(items[0].url); toast.success("Image uploaded"); }
      else toast.error(items[0]?.error || "Upload failed");
    } catch (e) { toast.error(e.friendlyMessage); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div>
      <label className="tq-label">{label}{required ? " *" : ""}</label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files); }}
        className={`border-2 border-dashed p-3 transition-colors ${drag ? "border-[#D4AF37] bg-[#F7F2E7]" : "border-[#E5DEC9] bg-white"}`}
        data-testid={`${testid}-dropzone`}
      >
        <div className="flex gap-2">
          <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="https://… or upload" data-testid={`${testid}-url`} className="tq-input flex-1" required={required && !value} />
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-upload-btn`} className="tq-btn-outline !py-2 !px-3 shrink-0">
            <Upload size={13} /> {busy ? "…" : "Upload"}
          </button>
          <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files)} data-testid={`${testid}-file-input`} />
        </div>
        {value ? (
          <img src={value} alt="" className="mt-3 w-full h-auto block border border-[#E5DEC9]" data-testid={`${testid}-preview`} />
        ) : (
          <p className="text-[11px] text-[#9a938a] mt-2 flex items-center gap-1.5"><Link2 size={11} /> Paste an image URL, click Upload, or drag & drop an image here.{hint ? ` ${hint}` : ""}</p>
        )}
      </div>
    </div>
  );
}

function BannerPreview({ f }) {
  const [device, setDevice] = useState("desktop");
  const [ratio, setRatio] = useState(null);
  const boxRef = useRef(null);
  const [avail, setAvail] = useState(600);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const update = () => setAvail(el.clientWidth - 24);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const W = DEVICES.find(([k]) => k === device)[2];
  const frameW = Math.min(avail, W);
  const src = device === "mobile" && f.image_mobile ? f.image_mobile : f.image_desktop;
  const scale = frameW / W;
  return (
    <div data-testid="banner-preview">
      <div className="flex items-center justify-between mb-2">
        <label className="tq-label !mb-0">Storefront Preview</label>
        <div className="flex gap-1">
          {DEVICES.map(([k, l, , Icon]) => (
            <button key={k} type="button" onClick={() => setDevice(k)} data-testid={`banner-preview-${k}`}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-semibold transition-colors ${device === k ? "bg-[#121212] text-[#D4AF37]" : "bg-white text-[#6E6D6A] hover:text-[#121212]"}`}>
              <Icon size={13} /> {l}
            </button>
          ))}
        </div>
      </div>
      <div ref={boxRef} className="bg-[#EDE7D9] p-3 flex justify-center">
        <div className="border border-[#D4AF37]/40 bg-[#121212] overflow-hidden transition-all"
          style={{ width: frameW, height: src && ratio ? Math.max(120, Math.round(W * ratio * scale)) : 160 }}
          data-testid="banner-preview-frame">
          {src ? (
            <div style={{ width: W, transform: `scale(${scale})`, transformOrigin: "top left", position: "relative" }}>
              <img key={src} src={src} alt="" onLoad={(e) => setRatio(e.target.naturalHeight / e.target.naturalWidth)} className="w-full h-auto block" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(18,18,18,.8), rgba(18,18,18,.25), transparent)" }} />
              <div style={{ position: "absolute", left: 60, right: 60, bottom: 56 }}>
                {f.tag && <p className="tq-overline" style={{ color: "#D4AF37", marginBottom: 10 }}>{f.tag}</p>}
                <p className="font-serif" style={{ color: "#fff", fontSize: 54, lineHeight: 1.05, maxWidth: 640 }}>{f.title || "Banner heading"}</p>
                {f.subtitle && <p style={{ color: "rgba(255,255,255,.8)", fontSize: 15, marginTop: 14, maxWidth: 520 }}>{f.subtitle}</p>}
                {f.button_text && <span className="tq-btn-gold" style={{ display: "inline-block", marginTop: 24 }}>{f.button_text}</span>}
              </div>
            </div>
          ) : (
            <p className="text-xs text-white/50 text-center pt-16">Add a {device === "mobile" ? "mobile or desktop" : "desktop"} image to preview</p>
          )}
        </div>
      </div>
      <p className="text-[11px] text-[#9a938a] mt-2">Preview keeps the image's original aspect ratio — banner height adjusts automatically, nothing is cropped, stretched or zoomed.</p>
    </div>
  );
}

function Editor({ item, onClose, onSaved }) {
  const [f, setF] = useState(item || { title: "", subtitle: "", tag: "", image_desktop: "", image_mobile: "", video: "", button_text: "", button_link: "", button2_text: "", button2_link: "", placement: "hero", start: "", end: "", active: true, order: 0 });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!f.image_desktop) { toast.error("Desktop banner image is required"); return; }
    setBusy(true);
    try {
      if (item?.id) await api.put(`/admin/banners/${item.id}`, f);
      else await api.post("/admin/banners", f);
      toast.success("Banner saved");
      onSaved();
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" data-testid="banner-editor">
      <div className="bg-[#FAF8F5] max-w-2xl mx-auto lg:my-10 min-h-screen lg:min-h-0 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl">{item?.id ? "Edit" : "New"} Banner</h2>
          <button onClick={onClose} aria-label="Close"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div><label className="tq-label">Heading *</label>
            <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="banner-title" className="tq-input" /></div>
          <div><label className="tq-label">Subtitle</label>
            <input value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} className="tq-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="tq-label">Tag / Overline</label>
              <input value={f.tag} onChange={(e) => setF({ ...f, tag: e.target.value })} placeholder="BRIDAL & FESTIVE" className="tq-input" /></div>
            <div><label className="tq-label">Placement</label>
              <select value={f.placement} onChange={(e) => setF({ ...f, placement: e.target.value })} data-testid="banner-placement" className="tq-input bg-white">
                {PLACEMENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
          </div>
          <ImageField label="Desktop Banner Image" required value={f.image_desktop} onChange={(v) => setF({ ...f, image_desktop: v })} testid="banner-desktop-image" />
          <ImageField label="Mobile Banner Image (optional — falls back to desktop)" value={f.image_mobile} onChange={(v) => setF({ ...f, image_mobile: v })} testid="banner-mobile-image" hint="Shown on screens ≤ 640px." />
          <div><label className="tq-label">Hero Video URL (optional — overrides image)</label>
            <input value={f.video || ""} onChange={(e) => setF({ ...f, video: e.target.value })} placeholder="https://… .mp4" data-testid="banner-video" className="tq-input" /></div>
          <BannerPreview f={f} />
          <div className="grid grid-cols-2 gap-3">
            <div><label className="tq-label">Button Text</label>
              <input value={f.button_text} onChange={(e) => setF({ ...f, button_text: e.target.value })} className="tq-input" /></div>
            <div><label className="tq-label">Button 2 Text (optional)</label>
              <input value={f.button2_text || ""} onChange={(e) => setF({ ...f, button2_text: e.target.value })} placeholder="Shop Best Sellers" data-testid="banner-btn2-text" className="tq-input" /></div>
            <div><label className="tq-label">Button 2 Link</label>
              <input value={f.button2_link || ""} onChange={(e) => setF({ ...f, button2_link: e.target.value })} placeholder="/collections/best-sellers" data-testid="banner-btn2-link" className="tq-input" /></div>
            <div><label className="tq-label">Button Link</label>
              <input value={f.button_link} onChange={(e) => setF({ ...f, button_link: e.target.value })} placeholder="/collections/sale" className="tq-input" /></div>
            <div><label className="tq-label">Start (schedule)</label>
              <input type="datetime-local" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="tq-input" /></div>
            <div><label className="tq-label">End (schedule)</label>
              <input type="datetime-local" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="tq-input" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="banner-active" className="accent-[#D4AF37] w-4 h-4" /> Active</label>
          <button type="submit" disabled={busy} data-testid="banner-save" className="tq-btn-dark w-full">{busy ? "Saving…" : "Save Banner"}</button>
        </form>
      </div>
    </div>
  );
}

function QuickUpload({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const ref = useRef(null);
  const handle = async (files) => {
    const list = [...(files || [])].filter((f) => f.type.startsWith("image/"));
    if (!list.length || busy) return;
    setBusy(true);
    try {
      const items = await uploadFiles(list);
      const ok = items.filter((i) => i.url && !i.error);
      for (let i = 0; i < ok.length; i++) {
        await api.post("/admin/banners", {
          title: (ok[i].filename || `Banner ${i + 1}`).replace(/\.[^.]+$/, ""),
          image_desktop: ok[i].url, placement: "hero", active: true, order: 1000 + i,
        });
      }
      if (ok.length) toast.success(`${ok.length} banner(s) created — edit titles/links below`);
      const failed = items.filter((i) => i.error);
      if (failed.length) toast.error(`${failed.length} file(s) skipped (${failed[0].error})`);
      onDone();
    } catch (e) { toast.error(e.friendlyMessage); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}
      onClick={() => ref.current?.click()}
      className={`mb-6 border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${drag ? "border-[#D4AF37] bg-[#F7F2E7]" : "border-[#D4AF37]/40 bg-white hover:bg-[#F7F2E7]"}`}
      data-testid="banner-quick-upload"
    >
      <Upload size={22} className="mx-auto text-[#C5A059] mb-2" />
      <p className="text-sm font-medium">{busy ? "Uploading… please wait" : "Drag & drop multiple banner images here, or click to browse"}</p>
      <p className="text-xs text-[#9a938a] mt-1">Each image becomes a new banner instantly · JPG, PNG, WebP up to 15 MB each</p>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handle(e.target.files)} data-testid="banner-quick-upload-input" />
    </div>
  );
}

export default function AdminBanners() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);
  const load = () => api.get("/admin/banners").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete banner?")) return; await api.delete(`/admin/banners/${id}`); toast.success("Deleted"); load(); };

  const persistOrder = async (arr) => { await Promise.all(arr.map((b, idx) => api.put(`/admin/banners/${b.id}`, { order: idx }))); };
  const move = async (i, dir) => {
    const arr = [...items]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setItems(arr);
    await persistOrder(arr);
  };
  const dropOn = async (targetId) => {
    if (!dragId || dragId === targetId) return;
    const arr = [...items];
    const from = arr.findIndex((b) => b.id === dragId);
    const to = arr.findIndex((b) => b.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setItems(arr);
    setDragId(null);
    await persistOrder(arr);
    toast.success("Banner order updated");
  };

  return (
    <div data-testid="admin-banners">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl">Banners</h1>
          <p className="text-sm text-[#6E6D6A] mt-1">Upload multiple images at once, drag rows to reorder. Banners display at their original aspect ratio — never cropped or stretched.</p>
        </div>
        <button onClick={() => setEditing({})} data-testid="banner-create-btn" className="tq-btn-dark !py-2.5"><Plus size={14} /> New Banner</button>
      </div>

      <QuickUpload onDone={load} />

      {!items ? <div className="h-48 bg-white animate-pulse" /> : items.length === 0 ? (
        <div className="bg-white border border-[#E5DEC9] p-12 text-center text-[#6E6D6A]">No banners yet. Drop images above or click New Banner.</div>
      ) : (
        <div className="space-y-3">
          {items.map((b, i) => (
            <div key={b.id}
              draggable
              onDragStart={() => setDragId(b.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => dropOn(b.id)}
              className={`bg-white border p-4 flex items-center gap-4 transition-colors ${dragId === b.id ? "border-[#D4AF37] opacity-60" : "border-[#E5DEC9]"}`}
              data-testid={`banner-row-${b.id}`}>
              <GripVertical size={16} className="text-[#C5A059] cursor-grab shrink-0" data-testid={`banner-drag-${b.id}`} />
              <span className="text-xs font-bold text-[#9a938a] w-5 shrink-0">{i + 1}</span>
              <img src={b.image_desktop} alt="" className="w-24 h-14 object-contain bg-[#F4EFE6] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{b.title}</p>
                <p className="text-xs text-[#9a938a]">{PLACEMENTS.find(([v]) => v === b.placement)?.[1] || b.placement} · {b.active ? <span className="text-[#1A3C34]">Active</span> : <span className="text-[#8B1E21]">Disabled</span>}{b.image_mobile ? " · Mobile image" : ""}{(b.start || b.end) && " · Scheduled"}</p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => move(i, -1)} aria-label="Move up" data-testid={`banner-up-${i}`} className="p-1.5 border border-[#E5DEC9] hover:border-[#D4AF37]"><ArrowUp size={14} /></button>
                <button onClick={() => move(i, 1)} aria-label="Move down" data-testid={`banner-down-${i}`} className="p-1.5 border border-[#E5DEC9] hover:border-[#D4AF37]"><ArrowDown size={14} /></button>
                <button onClick={() => setEditing(b)} aria-label="Edit" data-testid={`banner-edit-${i}`} className="p-1.5 border border-[#E5DEC9] hover:border-[#D4AF37]"><Pencil size={14} /></button>
                <button onClick={() => del(b.id)} aria-label="Delete" data-testid={`banner-delete-${i}`} className="p-1.5 border border-[#E5DEC9] hover:border-[#8B1E21] hover:text-[#8B1E21]"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <Editor item={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
